import CloseIcon from '@mui/icons-material/Close';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import IconButton from '@mui/material/IconButton';
import Controller from 'core/components/FormController';
import ToolTip from 'core/components/Tooltip';
import {
  FieldComponentProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/FieldsArrayWrapper';
import AutoCompletedInput from 'core/components/AutoComplete';
import Select from 'core/components/Select';
import {
  useClaimSettings,
} from 'pages/Dashboard/pages/Billing/hooks';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
  ClaimAttachmentReportTypeCodeSettingDTO,
} from 'dtos/claimAttachmentReportTypeCodeSettingDTO';
import {
  ClaimAttachmentTransmissionCodeSettingDTO,
} from 'dtos/claimAttachmentTransmissionCodeSettingDTO';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Input from 'pages/Dashboard/components/Input';
import {
  Button,
} from '@populateui/ui';
import isNil from 'lodash/isNil';
import PatientMediaListPopover from 'pages/Dashboard/pages/Billing/ClaimForm/PatientMediaListPopover';
import EditIcon from 'core/icons/edit';
import {
  usePatientGetMultimediaByMedia,
} from 'views/EMR/hooks/media';
import head from 'lodash/head';
import usePreviewMediaClick from 'pages/Dashboard/hooks/usePreviewMediaClick';
import PreviewOutlined from '@mui/icons-material/PreviewOutlined';
import clsx from 'clsx';
import keyBy from 'lodash/keyBy';
import Skeleton from 'core/components/Skeleton';

export default function AttachmentItem({
  index,
  onDelete,
  prefix,
}: FieldComponentProps) {
  const { control, watch, setValue } = useFormContext();
  const key = `${prefix}.${index}`;
  const showLabel = index === 0;
  const [
    transmissionCode,
    patientMultiMediaId,
  ] = watch([
    `${key}.transmissionCode`,
    `${key}.patientMultiMediaId`,
  ]);

  const {
    data: mediaList,
    isLoading: isLoadingMedia,
  } = usePatientGetMultimediaByMedia({ mediaId: patientMultiMediaId });

  const selectedMedia = head(mediaList?.list);
  const handleTableRowClick = usePreviewMediaClick();

  const {
    attachmentReportTypeCodeSettings = [],
    attachmentTransmissionCodeSettings = [],
  } = useClaimSettings();

  const reportTypeByCode = useMemo(
    () => keyBy(attachmentReportTypeCodeSettings, 'code'),
    [attachmentReportTypeCodeSettings],
  );

  const isFileUploadSupported = attachmentTransmissionCodeSettings?.find(
    (setting) => setting.code === transmissionCode?.code,
  )?.isFileUploadSupported ?? false;
  const hasTransmissionCode = !isNil(transmissionCode);

  useEffect(() => {
    if (isFileUploadSupported && isNil(transmissionCode?.isFileUploadSupported)) {
      setValue(`${key}.transmissionCode`, {
        ...transmissionCode,
        isFileUploadSupported: true,
      });
    }
  }, [isFileUploadSupported, transmissionCode, key]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <div className="flex items-end gap-3 w-full">
      <Controller
        name={`${key}.attachmentReportTypeCode`}
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => {
          const selectedReportType = reportTypeByCode[value ?? ''];

          return (
            <Wrapper
              id={`${key}.attachmentReportTypeCode`}
              className="w-0 flex-1 min-w-0 text-xs"
              label={showLabel ? 'Report Type' : ''}
              error={invalid}
              showErrorText={false}
            >
              <AutoCompletedInput<ClaimAttachmentReportTypeCodeSettingDTO>
                {...field}
                id="date-type"
                value={selectedReportType}
                onChange={(value) => {
                  onChange((value as ClaimAttachmentReportTypeCodeSettingDTO)?.code ?? null);
                }}
                options={attachmentReportTypeCodeSettings ?? []}
                placeholder="Select"
                className="w-full"
                error={invalid}
                getLabel={({ code, name }) => (
                  name ? `${code ?? ''} - ${name}` : `${code ?? ''}`
                )}
                isOptionEqualToValue={(option, value) => (
                  option.code === (value as ClaimAttachmentReportTypeCodeSettingDTO)?.code
                )}
                openOnFocus
              />
            </Wrapper>
          );
        }}
      />
      <Controller
        name={`${key}.transmissionCode`}
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState: { invalid } }) => (
          <Select<ClaimAttachmentTransmissionCodeSettingDTO>
            {...field}
            getValue={(option) => option?.code ?? ''}
            getLabel={({ code, name }) => `${code ?? ''} - ${name ?? ''}`}
            options={attachmentTransmissionCodeSettings ?? []}
            placeholder="Select"
            className="w-0 flex-1 min-w-0"
            id="date-type"
            muiClasses={{ select: '!py-[7px]' }}
            label={showLabel ? 'Transmission Code' : ''}
            error={invalid}
            showErrorText={false}
          />
        )}
      />
      <Wrapper
        id="attachment-input"
        className="w-0 flex-1 min-w-0 text-xs"
        label={showLabel ? 'Attachment Control Number/File' : ''}
      >
        <ToolTip
          title="Select a transmission code first"
          hide={hasTransmissionCode}
        >
          <span className="block w-full">
            {isFileUploadSupported && (
              <Controller
                name={`${key}.patientMultiMediaId`}
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                  <>
                    {isNil(value) ? (
                      <Button
                        className={clsx('w-full !max-w-[initial] !justify-start !pl-1', {
                          '!border-red-500 !text-red-500': invalid,
                        })}
                        id="attachment-input"
                        variant="outlined"
                        color="primary"
                        type="button"
                        disabled={!hasTransmissionCode}
                        onClick={(e) => {
                          if (!hasTransmissionCode) return;
                          setAnchorEl(e.currentTarget);
                        }}
                      >
                        <AttachFileIcon className="!w-4 !h-4 !text-inherit" />
                        Select patient media
                      </Button>
                    ) : (
                      <div className="flex items-center justify-between rounded p-1 min-w-0 flex-1 !h-8 border border-gray-200">
                        {isLoadingMedia ? (
                          <Skeleton className="!h-4" />
                        ) : (
                          <span className="truncate font-semibold">
                            {selectedMedia?.fileName}
                          </span>
                        )}

                        <div className="flex items-center">
                          <ToolTip title="Preview">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTableRowClick(selectedMedia ?? {});
                              }}
                            >
                              <PreviewOutlined className="!w-4 !h-4 text-gray-500" />
                            </IconButton>
                          </ToolTip>
                          <ToolTip title="Replace Attachment">
                            <IconButton
                              size="small"
                              color="info"
                              className="!p-1"
                              onClick={(event) => {
                                event.stopPropagation();
                                setAnchorEl(event.currentTarget);
                              }}
                            >
                              <EditIcon className="!w-5 !h-5 !text-gray-600" />
                            </IconButton>
                          </ToolTip>
                        </div>
                      </div>
                    )}
                    {!isNil(anchorEl) && hasTransmissionCode && (
                      <PatientMediaListPopover
                        mediaId={value}
                        formKey={prefix ?? ''}
                        anchorEl={anchorEl}
                        value={value}
                        onClose={() => setAnchorEl(null)}
                        onChange={onChange}
                      />
                    )}
                  </>
                )}
              />
            )}
            {!isFileUploadSupported && (
              <Controller
                name={`${key}.attachmentControlNumber`}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    className="w-full"
                    id="attachment-input"
                    placeholder="Enter Attachment Control Number"
                    error={invalid}
                    showErrorText={false}
                    disabled={!hasTransmissionCode}
                    inputProps={{ maxLength: 50 }}
                  />
                )}
              />
            )}
          </span>
        </ToolTip>
      </Wrapper>
      <ToolTip title="Remove">
        <span>
          <IconButton onClick={() => { onDelete(index); }}>
            <CloseIcon className="!w-4 !h-4" />
          </IconButton>
        </span>
      </ToolTip>
    </div>
  );
}
